import {
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SafeResourceUrlPipe } from '../../../shared/pipes/safe-resource-url.pipe';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { SearchSelectComponent } from '../../../shared/components/search-select/search-select.component';
import { VendorService } from '../../vendors/vendor.service';
import {
  PoService,
  VendorSkuMappingItem,
  VendorSkuMappingMode,
  VendorSkuMappingPreview,
} from '../po.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  Observable,
  firstValueFrom,
} from 'rxjs';
import { ProductsService } from '../../products/products.service';
import { withMatchedEan } from '../../../core/utils/current-ean.util';
import {
  hasPurchasePrice,
  priceNotSetMessage,
} from '../../../core/utils/price-status.util';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { environment } from '../../../../environments/environment';
import { IndentListService } from '../../indent-list/indent-list.service';
import {
  WarehouseSelectComponent,
  WarehouseOption,
} from '../../../shared/components/warehouse-select/warehouse-select.component';
import { QtyInputDirective } from '../../../shared/directives/qty-input.directive';
import { allowsDecimalQty } from '../../../shared/utils/quantity.util';
import { FormReloadService } from '../../../core/services/form-reload.service';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../../configuration/configuration.service';

/** What the buyer chose for one unmapped SKU in the mapping dialog. */
export type VendorSkuMappingDecision = 'map' | 'skip' | 'remove';

interface VendorSkuMappingDialogRow extends VendorSkuMappingItem {
  decision: VendorSkuMappingDecision;
}

interface VendorSkuMappingDialogResult {
  /** Product ids to map to the vendor. */
  map: string[];
  /** Product ids to post without a mapping (PROMPT mode only). */
  skip: string[];
  /** Lines (productVariantIds) the buyer chose to drop from the order. */
  removeVariantIds: string[];
}

interface VendorSkuMappingDialogState {
  /** submit: decisions ride on the save; review: "Map now" ahead of submit. */
  purpose: 'submit' | 'review';
  mode: VendorSkuMappingMode;
  vendorName: string;
  rows: VendorSkuMappingDialogRow[];
  /** Lines on the order (mapped + unmapped) — for the "n of m" headline. */
  total: number;
  busy: boolean;
  resolve: (result: VendorSkuMappingDialogResult | null) => void;
}

@Component({
  selector: 'app-create-po',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgSelectComponent,
    NgSelectModule,
    SearchSelectComponent,
    SafeResourceUrlPipe,
    WarehouseSelectComponent,
    QtyInputDirective,
    RouterLink,
  ],
  templateUrl: './create-po.component.html',
  styleUrl: './create-po.component.scss',
})
export class CreatePoComponent implements OnInit {
  /**
   * Whether this line may be keyed in fractions - bulk, weighable, bulk-child
   * or a kg/gm/ltr unit. Counted SKUs (PCS, NOS, PKT ...) stay whole. The
   * server applies the same rule on save.
   */
  allowsDecimal(source: any): boolean {
    return allowsDecimalQty(source);
  }

  @ViewChild('quantityInput') quantityInputRef!: ElementRef;
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private poService = inject(PoService);
  private vendorService = inject(VendorService);
  private productService = inject(ProductsService);
  private http = inject(HttpClient);
  // Preview State
  showPreview = false;
  previewUrl: SafeResourceUrl | null = null;
  private rawBlobUrl: string | null = null;
  isLoadingPreview = false;
  private confirmDialog = inject(ConfirmDialogService);
  private sanitizer = inject(DomSanitizer);
  private formReload = inject(FormReloadService);
  private isSaved = false;
  private indentListService = inject(IndentListService);
  todayDate = new Date().toISOString().split('T')[0];
  defaultDeliveryDate = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  defaultExpiryDate = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  poForm!: FormGroup;
  poItems: any[] = [];
  manufacturers: any[] = [];
  vendors: any[] = [];
  brands: any[] = [];
  departments: any[] = [];
  categories: any[] = [];
  subCategories: any[] = [];
  products: any[] = [];
  productSearchTerm: string = '';
  selectedVendor: any;
  selectedManufacturers: string[] = [];
  selectedBrands: string[] = [];
  selectedDepartment: string = '';
  selectedCategory: string | null = null;
  selectedSubCategories: string[] = [];
  allCostPrices: number[] = [];
  allMRPs: number[] = [];
  deliveryDate: string = this.defaultDeliveryDate;
  expiryDate: string = this.defaultExpiryDate;
  freightCharges: number = 0;
  roundOffAmount: number = 0;
  schemameDiscountAmount: number = 0;
  fixedDiscountAmount: number = 0;
  // Upload
  isUploading = false;
  uploadErrors: Array<{ row: number; code: string; error: string }> = [];

  // Add to your existing properties
  productCodeSearch$ = new Subject<string>();
  searchResults: any[] = [];
  isCodeSearching = false;
  /** "Search by EAN" box — resolves identity barcodes and price-generation EANs. */
  productEanSearch$ = new Subject<string>();
  eanSearchResults: any[] = [];
  isEanSearching = false;
  selectedSearchType: 'sku' | 'plu' | 'ean' | 'query' = 'sku';
  searchTypes = [
    { value: 'sku', label: 'SKU' },
    { value: 'plu', label: 'PLU' },
    { value: 'ean', label: 'EAN' },
    { value: 'query', label: 'General' },
  ];
  mode = 'create';
  poId: string = '';
  today = new Date().toISOString().split('T')[0];
  summary = {
    totalItems: 0,
    totalQuantity: 0,
    totalBasicCost: 0,
    totalGstAmount: 0,
    totalPoValue: 0,
    schemeDiscountAmount: 0,
    fixedDiscountAmount: 0,
    totalCessAmount: 0,
    lineItemDiscountAmount: 0,
  };
  productSearch$ = new Subject<string>();
  selectedProduct: any = null;
  selectedVariant: any = null;
  selectedQuantity: number = 1;
  isSearching = false;
  enteredCostPrice: number = 0;
  MRP: number = 0;
  // The store/warehouse this PO is raised for. Display-only: it follows the
  // header switcher for a new PO and the saved PO's own location when
  // editing/viewing. locationId feeds product-search SOH (this used to hold a
  // STOREID, which matched no location and zeroed SOH); storeId goes on the
  // PO payload.
  selectedWarehouse: WarehouseOption | null = null;
  documentLocationId: string | null = null;
  sourceLocationId: string = '';
  baseUrl = environment.apiUrl;
  fromDate: string = '';
  selectedIndents: string[] = [];
  indentOptions: { id: string; label: string }[] = [];

  // ── Page UI state (design: collapsible header, line filters, bulk dialog) ──
  headerOpen = true;
  lineFilter: 'all' | 'indent' | 'manual' = 'all';
  lineQuery = '';
  bulkOpen = false;
  bulkFile: File | null = null;
  dragOver = false;

  // New Pricing Modal State
  isPricingFormVisible = false;
  editingItem: any = null; // ✅ Track which item is being edited
  newPricing = {
    basePrice: 0,
    costPrice: 0,
    sellingPrice: 0,
    mrp: 0,
  };
  newPricingValidationError: string | null = null;
  // CP-from-MRP is opt-in per popup: OFF by default so keying an MRP never
  // silently rewrites a manually entered cost price. When enabled, the
  // margin % (seeded from the product master) drives CP = MRP * (1 - m/100).
  useMarginForCp = false;
  pricingMarginPercent = 0;

  // ── Vendor ↔ SKU mapping (setting po.vendorSkuMappingMode) ──────────────
  // STRICT: the add-product searches are scoped to the vendor's SKUs (the
  // long-standing behaviour). PROMPT / REQUIRED: any SKU can be added; lines
  // not mapped to the vendor are badged, and on submit the buyer decides per
  // SKU whether to map it to the vendor, post it unmapped (PROMPT only) or
  // drop it. The server applies the same rule when the PO is posted.
  private configurationService = inject(ConfigurationService);
  vendorSkuMappingMode: VendorSkuMappingMode = 'STRICT';
  mappingDialog: VendorSkuMappingDialogState | null = null;
  mappingCheckPending = false;
  private vendorMappingRefresh$ = new Subject<void>();
  private lastMappingSignature = '';
  /** Decisions collected from a server PO_UNMAPPED_SKUS rejection, used on the resubmit. */
  private pendingMappingDecisions: { map: string[]; skip: string[] } | null = null;

  /** True when any active SKU may be put on the order, not just the vendor's. */
  get openVendorScope(): boolean {
    return this.vendorSkuMappingMode !== 'STRICT';
  }

  /** Vendor filter for the product searches: none in the open modes. */
  get vendorIdForSearch(): string | undefined {
    if (this.openVendorScope) return undefined;
    return this.selectedVendor || undefined;
  }

  /** Lines the last mapping check found NOT supplied by the selected vendor. */
  get unmappedLineCount(): number {
    return this.poItems.filter((i) => i?.vendorMapped === false).length;
  }

  get openScopeHint(): string {
    return this.vendorSkuMappingMode === 'REQUIRED'
      ? 'must be mapped to the vendor, or removed, before the PO is submitted'
      : 'will be offered for mapping to the vendor when the PO is submitted';
  }

  ngOnInit(): void {
    if (this.mode === 'view') {
      this.poForm.disable();
    }
    this.poForm = this.fb.group({
      poNumber: ['', Validators.required],
      deliveryDate: [this.deliveryDate],
      expiryDate: [this.expiryDate],
      packingCharges: [0],
      freightCharges: [0],
      roundOffAmount: [0],
      schemeDiscountAmount: [0],
      fixedDiscountAmount: [0],
      // false = basic + GST extra (B2B default). Auto-fills from vendor's
      // saved preference when a vendor is selected.
      costPriceIncludesGst: [false],
    });

    this.poForm
      .get('deliveryDate')
      ?.valueChanges.subscribe((selectedDate: string) => {
        if (!selectedDate) return;
        if (this.mode === 'view') return;

        const today = new Date();
        const chosenDate = new Date(selectedDate);

        console.log('Chosen Date:', chosenDate);

        if (chosenDate < today) {
          // ❌ Show error and reset deliveryDate to today
          this.toastr.error(
            'Delivery date cannot be earlier than today.',
            'Invalid Date',
          );
          const formattedToday = today.toISOString().split('T')[0];

          this.poForm.patchValue(
            { deliveryDate: formattedToday },
            { emitEvent: false },
          );
        }

        /** ✅ Step 4: Also validate expiryDate whenever deliveryDate changes */
        const expiryDateValue = this.poForm.get('expiryDate')?.value;
        if (expiryDateValue) {
          const expiryDate = new Date(expiryDateValue);
          if (expiryDate < chosenDate) {
            this.toastr.error(
              'Expiry date cannot be earlier than delivery date.',
              'Invalid Date',
            );
            this.poForm.patchValue(
              { expiryDate: chosenDate.toISOString().split('T')[0] },
              { emitEvent: false },
            );
          }
        }
      });

    /** ✅ Step 5: Watch for Expiry Date changes */
    this.poForm
      .get('expiryDate')
      ?.valueChanges.subscribe((selectedDate: string) => {
        if (!selectedDate) return;

        const deliveryDateValue = this.poForm.get('deliveryDate')?.value;
        if (!deliveryDateValue) return;

        const deliveryDate = new Date(deliveryDateValue);
        const expiryDate = new Date(selectedDate);

        if (expiryDate < deliveryDate) {
          // ❌ Show error and reset expiryDate to deliveryDate
          this.toastr.error(
            'Expiry date cannot be earlier than delivery date.',
            'Invalid Date',
          );
          const formattedDelivery = deliveryDate.toISOString().split('T')[0];
          this.poForm.patchValue(
            { expiryDate: formattedDelivery },
            { emitEvent: false },
          );
        }
      });

    const id = this.route.snapshot.paramMap.get('id');
    const view = this.route.snapshot.queryParamMap.get('view');
    if (id) {
      this.poId = id;
      this.mode = view === 'true' ? 'view' : 'edit';

      this.loadPurchaseOrder();
    } else {
      this.mode = 'create';

      this.getNextPoNumber();
    }

    this.productSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 3),
        tap(() => (this.isSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({
              query: term,
              context: 'PO',
              // Scope results to the selected vendor's products only (backend
              // filters Products.vendorsId for context 'PO'). Left undefined
              // until a vendor is chosen.
              vendorId: this.vendorIdForSearch,
            })
            .pipe(
              map((res: any) => res?.data || []),
              catchError(() => of([])),
              tap(() => (this.isSearching = false)),
            ),
        ),
      )
      .subscribe((data) => {
        this.products = data;
      });

    this.productCodeSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 2),
        tap(() => (this.isCodeSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({
              [this.selectedSearchType]: term,
              context: 'PO',
              locationId: this.sourceLocationId,
              // Scope SKU/PLU/EAN search to the selected vendor's products
              // only (backend filters Products.vendorsId for context 'PO').
              vendorId: this.vendorIdForSearch,
            })
            .pipe(
              map((res: any) => res?.data || []),
              catchError(() => of([])),
              tap(() => (this.isCodeSearching = false)),
            ),
        ),
      )
      .subscribe((data) => {
        this.searchResults = data.map((item: any) => ({
          ...item,
          displayText: `${item.sku} - ${item.longDescription}`,
        }));
      });

    this.productEanSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.trim().length >= 3),
        tap(() => (this.isEanSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({
              ean: term.trim(),
              context: 'PO',
              locationId: this.sourceLocationId,
              // Vendor-scoped like the SKU search above.
              vendorId: this.vendorIdForSearch,
            })
            .pipe(
              map((res: any) => withMatchedEan(res?.data || [], term)),
              catchError(() => of([])),
              tap(() => (this.isEanSearching = false)),
            ),
        ),
      )
      .subscribe((data) => {
        this.eanSearchResults = data;
      });

    this.configurationService
      .getValue(SETTING_KEYS.PO_VENDOR_SKU_MAPPING_MODE)
      .subscribe((value) => {
        this.vendorSkuMappingMode =
          value === 'PROMPT' || value === 'REQUIRED' ? value : 'STRICT';
        this.scheduleVendorMappingRefresh();
      });

    // Badge refresh is debounced and skipped when the set of lines has not
    // changed, so quantity edits (which also call updateSummary) cost nothing.
    this.vendorMappingRefresh$
      .pipe(debounceTime(400))
      .subscribe(() => this.refreshVendorMappingBadges());

    this.loadInitialData();
  }

  loadInitialData() {
    this.poService
      .getDepartments()
      .subscribe((res: any) => (this.departments = res?.data || []));
    this.vendorService
      .getAllVendorsList()
      .subscribe((res: any) => (this.vendors = res || []));
  }

  loadPurchaseOrder() {
    this.poService.getPoById(this.poId).subscribe((res: any) => {
      const poData = res || {};

      // A saved PO keeps ITS OWN store/warehouse, whatever the viewer is
      // currently working as.
      this.documentLocationId = poData?.store?.locationId ?? null;

      this.poForm.patchValue({
        poNumber: poData.poNumber,
        deliveryDate: poData.deliveryDate?.split('T')[0] || '',
        expiryDate: poData.expiryDate?.split('T')[0] || '',
        packingCharges: poData.packingCharges || 0,
        freightCharges: poData.freightCharges || 0,
        roundOffAmount: poData.roundOffAmount || 0,
        schemeDiscountAmount: poData.schemeDiscountAmount || 0,
        fixedDiscountAmount: poData.fixedDiscountAmount || 0,
      });

      this.fromDate = poData.indentDate || '';

      // 2. Load the selected indent IDs (used to set the multi-select's selected values)
      this.selectedIndents = poData.indentIds || [];

      // 3. Populate indentOptions (used to provide the display text for the selected IDs)
      if (poData.purchaseOrderIndents) {
        this.indentOptions = poData.purchaseOrderIndents.map((poi: any) => ({
          id: poi.indentId,
          label: `${poi.indent.indentNumber || '-'} | ${poi.indent.store?.name || '-'
            } | ${(poi.indent.createdAt || '').slice(0, 10)}`,
        }));

        // CRITICAL: If the indents are loaded, and the fromDate is set,
        // we should try to load all indents for that date in case the user wants to select a new one.
        if (this.fromDate) {
          this.loadAllIndentsForDate(this.fromDate);
        }
      }
      // Derive IDs from items if missing at root
      let manufacturerIds = poData.manufacturerIds || [];
      let brandIds = poData.brandIds || [];
      let departmentIds = poData.departmentIds || [];
      let categoryIds = poData.categoryIds || [];
      let subCategoryIds = poData.subCategoryIds || [];

      if (
        (!manufacturerIds || manufacturerIds.length === 0) &&
        poData.items?.length
      ) {
        manufacturerIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.manufacturerId)
              .filter((id: any) => !!id),
          ),
        ];
      }
      if ((!brandIds || brandIds.length === 0) && poData.items?.length) {
        brandIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.brandId)
              .filter((id: any) => !!id),
          ),
        ];
      }
      if (
        (!departmentIds || departmentIds.length === 0) &&
        poData.items?.length
      ) {
        departmentIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.departmentId) // Singular in product
              .filter((id: any) => !!id),
          ),
        ];
      }
      if ((!categoryIds || categoryIds.length === 0) && poData.items?.length) {
        categoryIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.categoryId) // Singular in product
              .filter((id: any) => !!id),
          ),
        ];
      }
      if (
        (!subCategoryIds || subCategoryIds.length === 0) &&
        poData.items?.length
      ) {
        subCategoryIds = [
          ...new Set(
            poData.items
              .map((i: any) => i.productVariant?.product?.subCategoryId) // Singular in product
              .filter((id: any) => !!id),
          ),
        ];
      }

      this.selectedVendor = poData.vendorId;

      // Restore the cost-price convention this PO was created with so
      // subsequent edits recompute under the same semantics.
      this.poForm.patchValue(
        { costPriceIncludesGst: !!poData.costPriceIncludesGst },
        { emitEvent: false },
      );

      // Assign derived or existing IDs
      this.selectedManufacturers = manufacturerIds;
      this.selectedBrands = brandIds;

      // Handle Department
      this.selectedDepartment = departmentIds;

      // Load Manufacturers and then populate Brands
      if (this.selectedVendor) {
        this.vendorService
          .getManufacturerDetails(this.selectedVendor)
          .subscribe((res: any) => {
            this.manufacturers = res || [];

            // Populate brands based on selected manufacturers
            if (
              this.selectedManufacturers &&
              this.selectedManufacturers.length > 0
            ) {
              this.brands = this.manufacturers
                .filter((m) => this.selectedManufacturers.includes(m.id))
                .flatMap((m) => m.brands || []);
            }
          });
      }

      if (departmentIds.length > 0) {
        // Load categories for the first department found
        // Note: If multiple departments are selected, currently we only load for the first one due to API limitations or UI logic
        this.poService
          .getCategoriesByDepartment(departmentIds[0])
          .subscribe((res: any) => {
            this.categories = res?.data || [];
          });
      }

      // Handle Category
      // UI uses single select for Category? Let's check.
      // HTML: [(ngModel)]="selectedCategory" ... [multiple] is NOT present.
      // So selectedCategory should be a single string (ID).
      // We pick the first one if multiple are found in items.
      this.selectedCategory = categoryIds.length > 0 ? categoryIds[0] : null;

      if (this.selectedCategory) {
        this.poService
          .getSubCategoriesByCategory(this.selectedCategory)
          .subscribe((res: any) => {
            this.subCategories = res?.data || [];
          });
      }

      this.selectedSubCategories = subCategoryIds;
      this.poItems = poData.items || [];
      this.poItems.forEach((item) => {
        if (item.id && !this.isValidUUID(item.id)) {
          console.warn(`Item id has invalid UUID:`, item.id);
        }
        item.sku =
          item?.productVariant?.sku ||
          item?.sku ||
          item?.productVariant?.product?.sku ||
          '';
        item.pricing = item?.productVariant?.pricing || item.pricing || [];
        item.variantName = item?.productVariant?.variantName || item.variantName || '';
        const gstPercent = item.gstPercent || 0;
        const cessRate = Number(item.cessRate || 0);
        const costPrice = item.costPrice || 0;
        const qty = Number(item.quantity || 0) + Number(item.indentQuantity || 0);
        const totalRate = gstPercent + cessRate;

        // actualCostPrice is the STORED basic cost price; only back-calc if
        // the field is missing on a legacy record.
        if (item.actualCostPrice == null || +item.actualCostPrice === 0) {
          item.actualCostPrice = +(costPrice / (1 + totalRate / 100)).toFixed(4);
        } else {
          item.actualCostPrice = Number(item.actualCostPrice);
        }

        item.gstAmount = Number(item.gstAmount || 0);
        item.cessAmount = Number(item.cessAmount || 0);

        // Back-fill basicCostTotal if missing on legacy records.
        if (item.basicCostTotal == null) {
          const lineGross = +(costPrice * qty).toFixed(3);
          item.basicCostTotal = +(lineGross - item.gstAmount - item.cessAmount).toFixed(3);
        }

        // ✅ Robust promotion: Check item.pricing if productVariant is missing
        const pricing = item.pricing?.[0];
        item.mrp = Number(item.mrp || pricing?.mrp || 0);
        item.sellingPrice = Number(item.sellingPrice || pricing?.sellingPrice || 0);

        item.total = Number(item.totalPrice || item.total || 0);

        // ✅ Store metadata for New Pricing modal support from SKU clicks
        item.productData = item.productVariant?.product || item.productData || null;
        item.variantData = item.productVariant || item.variantData || null;
      });
      this.refreshDraftLinesFromMaster(poData);
      this.updateSummary();
    });
  }

  /**
   * DRAFT only: pull today's product-master values (GST %, cess, margin)
   * into every line and recompute its tax, so a slab corrected after the
   * draft was saved is reflected when the draft is reopened.
   *
   * The backend attaches the `current*` fields to DRAFT documents only, so a
   * POSTED PO keeps its snapshot untouched. Runs in view mode as well — a
   * draft opened read-only must still show today's rates, and nothing is
   * written until the user edits and saves. Prices the user typed (cost
   * price, MRP) are never overwritten.
   */
  private refreshDraftLinesFromMaster(poData: any): void {
    if (poData?.status !== 'DRAFT') return;

    const changed: string[] = [];
    for (const item of this.poItems) {
      const hasSnapshot =
        item.currentGstPercent != null || item.currentCessRate != null;
      if (!hasSnapshot) continue;

      const oldGst = +(item.gstPercent || 0);
      const oldCess = +(item.cessRate || 0);
      const newGst =
        item.currentGstPercent != null ? +item.currentGstPercent : oldGst;
      const newCess = +(item.currentCessRate ?? 0);
      const diffs: string[] = [];

      if (newGst !== oldGst) {
        diffs.push(`GST ${oldGst}% → ${newGst}%`);
        item.gstPercent = newGst;
      }
      if (newCess !== oldCess) {
        diffs.push(`Cess ${oldCess}% → ${newCess}%`);
        item.cessRate = newCess;
      }
      if (
        item.currentMarginPercent != null &&
        item.marginPercent != null &&
        +item.currentMarginPercent !== +item.marginPercent
      ) {
        diffs.push(`Margin ${+item.marginPercent}% → ${+item.currentMarginPercent}%`);
        item.marginPercent = +item.currentMarginPercent;
      }

      // Strip the snapshot so it never rides along into the save payload.
      for (const k of Object.keys(item)) {
        if (k.startsWith('current')) delete item[k];
      }

      if (diffs.length) {
        this.onQuantityChange(item); // recompute line tax + totals
        changed.push(`${item.sku || item.skuCode || ''} (${diffs.join(', ')})`);
      }
    }

    if (changed.length) {
      const shown = changed.slice(0, 5).join('; ');
      const more = changed.length > 5 ? ` and ${changed.length - 5} more` : '';
      this.toastr.info(
        `${changed.length} line(s) updated from the SKU master: ${shown}${more}. Save the draft to keep these values.`,
        'Draft refreshed',
        { timeOut: 10000 },
      );
    }
  }

  loadAllIndentsForDate(date: string): void {
    this.indentListService.getIndentByStoreIds([], date, 'POSTED').subscribe({
      next: (res: any[]) => {
        const list = Array.isArray(res) ? res : [];
        const newOptions = list.map((item: any) => ({
          id: item.id,
          label: `${item.indentNumber || '-'} | ${item.store?.name || '-'} | ${(
            item.createdAt || ''
          ).slice(0, 10)}`,
        }));

        // Merge existing selected indents (from PO data or previous selections) with all indents from the API
        const uniqueOptions = [...this.indentOptions];
        const existingIds = new Set(uniqueOptions.map((o) => o.id));

        newOptions.forEach((option) => {
          if (!existingIds.has(option.id)) {
            uniqueOptions.push(option);
          }
        });
        this.indentOptions = uniqueOptions;

        // Only show toast if the list was actually updated from the API
        if (newOptions.length > 0) {
          this.toastr.success('Available Indents updated for selected date');
        }
      },
      error: () => this.toastr.error('Failed to load indents from API'),
    });
  }

  onManufacturersChange() {
    // Update brands list based on selected manufacturers
    this.brands = this.manufacturers
      .filter((m) => this.selectedManufacturers.includes(m.id))
      .flatMap((m) => m.brands || []);

    // Reset brand selection if current selection is not valid for new manufacturers
    if (this.selectedBrands && this.selectedBrands.length > 0) {
      const validBrandIds = this.brands.map((b) => b.id);
      this.selectedBrands = this.selectedBrands.filter((brandId) =>
        validBrandIds.includes(brandId),
      );
    }
  }

  onDepartmentChange() {
    this.categories = [];
    this.subCategories = [];
    this.selectedCategory = null;
    this.selectedSubCategories = [];
    if (this.poForm.get('category')) {
      this.poForm.get('category')?.reset(null);
    }
    if (this.poForm.get('subCategory')) {
      this.poForm.get('subCategory')?.reset([]);
    }

    if (this.selectedDepartment && this.selectedDepartment.length > 0) {
      this.poService
        .getCategoriesByDepartment(this.selectedDepartment)
        .subscribe((res: any) => {
          this.categories = res?.data || [];
          console.log('[DepartmentChange] Loaded Categories:', this.categories);
        });
    }
  }

  onCategoryChange() {
    // Reset dependent selections
    this.subCategories = [];
    this.selectedSubCategories = [];
    if (this.selectedCategory) {
      // Load subcategories for selected category
      this.poService
        .getSubCategoriesByCategory(this.selectedCategory)
        .subscribe((res: any) => {
          this.subCategories = res?.data || [];
        });
    }
  }

  onFromDateChange(): void {
    this.selectedIndents = [];
    this.indentOptions = [];
    // Load all available indents for the newly selected date
    this.loadAllIndentsForDate(this.fromDate);
  }
  loadFilteredProducts() {
    const filters: any = {};

    // Build filter object with current selections
    if (this.selectedVendor) filters.vendorId = this.selectedVendor;
    if (this.selectedManufacturers?.length)
      filters.manufacturerIds = this.selectedManufacturers;
    if (this.selectedBrands?.length) filters.brandIds = this.selectedBrands;
    if (this.selectedDepartment && this.selectedDepartment.length > 0)
      filters.departmentIds = [this.selectedDepartment];
    if (this.selectedCategory?.length)
      filters.categoryIds = [this.selectedCategory];
    if (this.selectedSubCategories?.length)
      filters.subCategoryIds = this.selectedSubCategories;
    if (this.selectedIndents?.length) filters.indentIds = this.selectedIndents;

    // Clean up empty filters
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete filters[key];
      }
    });

    // IMPORTANT: If no vendor selected, clear table and return
    if (!this.selectedVendor) {
      this.poItems = [];
      this.updateSummary();
      return;
    }

    // If only vendor is selected but no other filters, we might want to show all vendor products
    // or clear the table - depends on your business logic
    if (Object.keys(filters).length === 1 && filters.vendorId) {
      // Uncomment the line below if you want to clear table when only vendor is selected
      // this.poItems = [];
      // this.updateSummary();
      // return;
    }

    // Get filtered products from API
    this.poService.getFilteredProducts(filters).subscribe({
      next: (res: any) => {
        const data = res || [];

        // CRITICAL: Clear the existing table first - this ensures proper filtering
        this.poItems = [];

        // Add all filtered products to table
        let addedCount = 0;
        let mergedCount = 0;
        const unpriced: string[] = [];
        for (const product of data) {
          const variants = product.variants || [];
          for (const variant of variants) {
            const merged = this.addItemToTable(product, variant);
            if (merged === null) unpriced.push(product.sku || product.longDescription);
            else if (merged) mergedCount++;
            else addedCount++;
          }
        }

        // Update summary after adding all items
        this.updateSummary();

        if (unpriced.length) {
          this.toastr.warning(
            `${unpriced.length} SKU(s) skipped — no cost price / MRP set yet: ${unpriced.slice(0, 5).join(', ')}${unpriced.length > 5 ? ', …' : ''}`,
            'Price required',
          );
        }

        // Show user feedback
        if (addedCount === 0 && mergedCount === 0) {
          this.toastr.info(
            'No items found matching the selected filters',
            'No Results',
          );
        } else if (mergedCount > 0) {
          this.toastr.success(
            `${addedCount} item(s) loaded; ${mergedCount} duplicate SKU(s) already in the list were merged (quantities summed).`,
            'Items Loaded',
          );
        } else {
          this.toastr.success(
            `${addedCount} items loaded based on current filters`,
            'Items Loaded',
          );
        }
      },
      error: (error) => {
        console.error('Error loading filtered products:', error);
        this.toastr.error('Failed to load filtered products', 'Error');
      },
    });
  }

  onVendorChange() {
    this.uploadErrors = [];
    if (this.selectedVendor) {
      this.poItems = [];
      this.updateSummary();
      this.loadVendorManufacturerDetails(this.selectedVendor);
      // Pull the vendor's cost-price convention as the default for this PO.
      const vendor = this.vendors.find((v: any) => v.id === this.selectedVendor);
      if (vendor) {
        this.poForm.patchValue(
          { costPriceIncludesGst: !!vendor.costPriceIncludesGst },
          { emitEvent: false },
        );
      }
    } else {
      this.manufacturers = [];
      this.brands = [];
      this.categories = [];
      this.subCategories = [];
      this.products = [];
      this.selectedManufacturers = [];
      this.selectedBrands = [];
      this.selectedCategory = '';
      this.selectedSubCategories = [];
      this.poItems = []; // ✅ Clear the table
      this.updateSummary(); // ✅ Reset summary
    }
  }

  downloadTemplate() {
    if (!this.selectedVendor) return;
    this.poService.downloadTemplate(this.selectedVendor).subscribe({
      next: (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'po_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully.');
      },
      error: () => this.toastr.error('Failed to download template.'),
    });
  }

  onFileSelected(event: any) {
    const file: File = event.target.files?.[0];
    this.uploadLineItems(file, () => (event.target.value = ''));
  }

  /** Parse an XLSX of line items against the selected vendor and append rows. */
  uploadLineItems(file: File | null | undefined, done?: (ok: boolean) => void) {
    if (!file || !this.selectedVendor) return;
    this.isUploading = true;
    this.uploadErrors = [];
    this.poService.parseUpload(file, this.selectedVendor).subscribe({
      next: (res: any) => {
        this.isUploading = false;
        this.uploadErrors = res.errors || [];
        // Apply vendor's cost-price convention so onQuantityChange below
        // recomputes each row under the right semantics.
        if (typeof res.costPriceIncludesGst === 'boolean') {
          this.poForm.patchValue(
            { costPriceIncludesGst: res.costPriceIncludesGst },
            { emitEvent: false },
          );
        }
        const validRows: any[] = res.rows || [];
        for (const row of validRows) {
          const existingIndex = this.poItems.findIndex(
            (i) => i.variantId === row.variantId,
          );
          if (existingIndex >= 0) {
            this.poItems[existingIndex].quantity += row.quantity;
            if (typeof row.vendorMapped === 'boolean')
              this.poItems[existingIndex].vendorMapped = row.vendorMapped;
            this.onQuantityChange(this.poItems[existingIndex]);
          } else {
            const newItem = {
              variantId: row.variantId,
              productVariantId: row.variantId,
              productId: row.productId,
              // Outside STRICT mode the sheet may carry SKUs the vendor does
              // not supply yet; they are badged and offered for mapping on submit.
              vendorMapped:
                typeof row.vendorMapped === 'boolean' ? row.vendorMapped : undefined,
              sku: row.code,
              skuCode: row.code,
              variantName: row.name,
              quantity: row.quantity,
              freeQuantity: row.freeQty,
              indentQuantity: 0,
              discountPercent: row.discountPercent,
              discountAmount: +(
                (Number(row.costPrice) * Number(row.discountPercent)) /
                100
              ).toFixed(2),
              costPrice: row.costPrice,
              actualCostPrice: row.actualCostPrice,
              mrp: row.mrp,
              sellingPrice: row.sellingPrice || 0,
              gstPercent: row.gstPercent,
              gstAmount: row.gstAmount,
              cessRate: row.cessRate,
              cessAmount: row.cessAmount,
              total: row.total,
              pricing: row.pricing || [
                {
                  costPrice: row.costPrice,
                  mrp: row.mrp,
                  sellingPrice: row.sellingPrice || 0,
                },
              ],
              productData: {
                id: row.productId,
                marginPercentage: row.marginPercentage,
                purchaseGstRate: { value: String(row.gstPercent) },
                cessRate: String(row.cessRate ?? 0),
              },
              variantData: {
                id: row.variantId,
                pricing: row.pricing || [
                  {
                    costPrice: row.costPrice,
                    mrp: row.mrp,
                    sellingPrice: row.sellingPrice || 0,
                  },
                ],
              },
            };
            this.poItems.push(newItem);
            this.onQuantityChange(newItem);
          }
        }
        this.updateSummary();
        if (validRows.length > 0)
          this.toastr.success(`${validRows.length} item(s) loaded from file.`);
        const unmappedRows = validRows.filter((r) => r.vendorMapped === false).length;
        if (unmappedRows > 0)
          this.toastr.info(
            `${unmappedRows} uploaded SKU(s) are not mapped to ${this.selectedVendorName || 'this vendor'} — they ${this.openScopeHint}.`,
            'Unmapped SKUs',
            { timeOut: 8000 },
          );
        if (res.errors?.length > 0)
          this.toastr.warning(
            `${res.errors.length} row(s) had errors — see details below.`,
          );
        done?.(true);
      },
      error: (err: any) => {
        this.isUploading = false;
        this.toastr.error(err?.error?.message || 'Upload failed.');
        done?.(false);
      },
    });
  }

  // ── Bulk line items dialog ─────────────────────────────────────────────
  openBulk(): void {
    if (!this.selectedVendor) {
      this.toastr.info('Select a vendor first — the template is scoped to its SKUs.');
      return;
    }
    this.bulkFile = null;
    this.dragOver = false;
    this.bulkOpen = true;
  }

  closeBulk(): void {
    if (this.isUploading) return;
    this.bulkOpen = false;
    this.bulkFile = null;
  }

  onBulkFilePicked(event: any): void {
    const file: File | undefined = event.target.files?.[0];
    if (file) this.bulkFile = file;
    event.target.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onBulkDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      this.toastr.warning('Please drop an .xlsx or .xls file.');
      return;
    }
    this.bulkFile = file;
  }

  importBulk(): void {
    if (!this.bulkFile) return;
    this.uploadLineItems(this.bulkFile, (ok) => {
      if (ok) {
        this.bulkOpen = false;
        this.bulkFile = null;
      }
    });
  }

  onDiscountChange(item: any) {
    const discPct = Number(item.discountPercent || 0);
    item.discountAmount = +(
      (Number(item.costPrice || 0) * discPct) /
      100
    ).toFixed(2);
    this.onQuantityChange(item);
  }

  loadVendorManufacturerDetails(vendorId: any) {
    this.vendorService
      .getManufacturerDetails(vendorId)
      .subscribe((res: any) => {
        this.manufacturers = res || [];
      });
  }

  searchProducts(type: string, term: string): Observable<any[]> {
    if (!term || term.length < 2) {
      return of([]); // Return an empty observable if term is too short
    }

    const params = new HttpParams().set('type', type).set('query', term);

    return this.http.get<any[]>('http://your-api/search', { params });
  }

  /**
   * Returns true when merged into an existing line, false when a new line was
   * added, and null when the variant has no usable price yet (barcode tagged,
   * pricing deferred) — a PO line at ₹0 must never be created silently.
   */
  addItemToTable(product: any, variant: any): boolean | null {
    if (!hasPurchasePrice(variant)) return null;

    const gstRate = parseFloat(product?.purchaseGstRate?.value || '0');
    const pricing0 = variant?.pricing?.[0] || {};
    const costPrice = parseFloat(pricing0.costPrice || '0');
    // basicCostPrice is independently stored; fall back to back-calc only if
    // a legacy record doesn't have it.
    const storedBasic = parseFloat(pricing0.basePrice || '0');
    const basicCostPrice =
      storedBasic > 0
        ? storedBasic
        : +(costPrice / (1 + (gstRate + (parseFloat(product?.cessRate || '0') || 0)) / 100)).toFixed(4);
    const cessRate = parseFloat(product?.cessRate || '0');
    let quantity = product?.innerPackaging || 1;
    const indentQty = product.indentTotalQty || 0;
    if (indentQty > 0) {
      quantity = 0;
    }
    const totalQty = quantity + indentQty;
    const breakup = this.computeLineTaxBreakup(
      basicCostPrice,
      costPrice,
      totalQty,
      gstRate,
      cessRate || 0,
    );
    const actualCost = breakup.actualCostPrice;
    const basicCostTotal = breakup.basicCostTotal;
    const gstAmount = breakup.gstAmount;
    const cessAmount = breakup.cessAmount;
    const total = breakup.lineGross;

    // Create item with basic properties - API filtering means we don't need complex metadata
    const newItem = {
      // Core properties for PO functionality
      pricing: variant.pricing || [],
      productVariantId: variant.id,
      variantName: variant.variantName,
      quantity,
      costPrice,
      actualCostPrice: actualCost,
      mrp: Number(variant.pricing?.[0]?.mrp || 0),
      sellingPrice: Number(variant.pricing?.[0]?.sellingPrice || 0),
      gstPercent: gstRate,
      gstAmount,
      total,
      basicCostTotal,
      innerPackaging: product?.innerPackaging || 1,
      cessRate,
      cessAmount,
      freeQuantity: variant.i || 0,
      indentQuantity: product.indentTotalQty || 0,

      // ✅ Add SKU codes for table display
      sku: variant.sku || product.sku || '',

      // Optional: Store minimal metadata for display purposes only
      productId: product.id,
      manufacturerName: product.manufacturer?.name || '',
      brandName: product.brand?.name || '',
      departmentName: product.department?.name || '',
      categoryName: product.category?.name || '',
      subCategoryName: product.subCategory?.name || '',

      // Store full product data for reference if needed
      productData: product,
      variantData: variant,
      marginPercentage: product.marginPercentage || 0,
    };

    // A PO/GRN should carry ONE line per SKU. When the same variant arrives
    // again (e.g. it is on more than one selected indent), merge into the
    // existing row and sum the quantities instead of adding a duplicate line.
    const existingIndex = this.poItems.findIndex(
      (i) => i.productVariantId === newItem.productVariantId,
    );
    if (existingIndex !== -1) {
      const existing = this.poItems[existingIndex];
      existing.quantity = Number(existing.quantity || 0) + Number(quantity || 0);
      existing.indentQuantity =
        Number(existing.indentQuantity || 0) + Number(newItem.indentQuantity || 0);
      existing.freeQuantity =
        Number(existing.freeQuantity || 0) + Number(newItem.freeQuantity || 0);
      this.onQuantityChange(existing);
      this.updateSummary();
      return true; // merged a duplicate
    }

    this.poItems.push(newItem);
    this.updateSummary();
    return false; // new row
  }

  removeItem(index: number) {
    this.selectedItems.delete(this.poItems[index]);
    this.poItems.splice(index, 1);
    this.updateSummary();
  }

  // Multi-select line items for bulk delete
  selectedItems = new Set<any>();

  isItemSelected(item: any): boolean {
    return this.selectedItems.has(item);
  }

  toggleItemSelection(item: any): void {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
  }

  get selectedCount(): number {
    return this.poItems.reduce(
      (n: number, it: any) => n + (this.selectedItems.has(it) ? 1 : 0),
      0
    );
  }

  get allItemsSelected(): boolean {
    return this.poItems.length > 0 && this.selectedCount === this.poItems.length;
  }

  toggleSelectAll(checked: boolean): void {
    this.selectedItems.clear();
    if (checked) {
      this.poItems.forEach((it: any) => this.selectedItems.add(it));
    }
  }

  // ── Line grid helpers (filters, counts, per-row delete) ────────────────
  /** Rows shown in the grid after the All / From indents / Manual pills and the text filter. */
  get visibleItems(): { item: any; index: number }[] {
    const q = (this.lineQuery || '').trim().toLowerCase();
    const out: { item: any; index: number }[] = [];
    this.poItems.forEach((item, index) => {
      const fromIndent = Number(item?.indentQuantity || 0) > 0;
      if (this.lineFilter === 'indent' && !fromIndent) return;
      if (this.lineFilter === 'manual' && fromIndent) return;
      if (q) {
        const hay = `${item?.sku || ''} ${item?.skuCode || ''} ${item?.variantName || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.push({ item, index });
    });
    return out;
  }

  get indentLineCount(): number {
    return this.poItems.filter((i) => Number(i?.indentQuantity || 0) > 0).length;
  }

  get manualLineCount(): number {
    return this.poItems.length - this.indentLineCount;
  }

  get lineCountLabel(): string {
    const shown = this.visibleItems.length;
    const all = this.poItems.length;
    const word = all === 1 ? 'line' : 'lines';
    return shown === all ? `${all} ${word}` : `${shown} of ${all} lines`;
  }

  get totalOrderedQty(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.quantity || 0), 0);
  }

  get totalIndentQty(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.indentQuantity || 0), 0);
  }

  get totalFreeQty(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.freeQuantity || 0), 0);
  }

  /** Sum of line gross values before the order-level scheme / fixed discounts. */
  get grossLineTotal(): number {
    return this.poItems.reduce((n, i) => n + Number(i?.total || 0), 0);
  }

  /** Line discounts + scheme + fixed — what the summary strip shows as DISCOUNT. */
  get totalDiscount(): number {
    return (
      Number(this.summary.lineItemDiscountAmount || 0) +
      Number(this.poForm?.value?.schemeDiscountAmount || 0) +
      Number(this.poForm?.value?.fixedDiscountAmount || 0)
    );
  }

  get selectedVendorName(): string {
    if (!this.selectedVendor) return '';
    const id = typeof this.selectedVendor === 'object' ? this.selectedVendor?.id : this.selectedVendor;
    return this.vendors.find((v) => v.id === id)?.name || '';
  }

  get selectedManufacturerLabel(): string {
    const ids = this.selectedManufacturers || [];
    if (!ids.length) return '';
    const names = ids
      .map((id) => this.manufacturers.find((m) => m.id === id)?.name)
      .filter(Boolean) as string[];
    if (!names.length) return '';
    return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
  }

  get categoryLabel(): string {
    const dep = (this.selectedDepartment as any) || [];
    const depIds: string[] = Array.isArray(dep) ? dep : dep ? [dep] : [];
    const depName = depIds.length
      ? this.departments.find((d) => d.id === depIds[0])?.name
      : '';
    const catName = this.selectedCategory
      ? this.categories.find((c) => c.id === this.selectedCategory)?.name
      : '';
    return [depName, catName].filter(Boolean).join(' › ');
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
    this.lineQuery = '';
  }

  clearSelectedProduct(): void {
    this.selectedProduct = null;
    this.selectedVariant = null;
    this.selectedQuantity = 1;
    this.enteredCostPrice = 0;
    this.MRP = 0;
    this.allCostPrices = [];
    this.allMRPs = [];
  }

  removeLine(item: any): void {
    if (this.isViewMode) return;
    const idx = this.poItems.indexOf(item);
    if (idx < 0) return;
    this.poItems.splice(idx, 1);
    this.selectedItems.delete(item);
    this.updateSummary();
  }

  async clearLines(): Promise<void> {
    if (this.isViewMode || !this.poItems.length) return;
    const n = this.poItems.length;
    const proceed = await this.confirmDialog.confirm({
      title: `Clear all ${n} line item${n === 1 ? '' : 's'}?`,
      message:
        'Quantities, free quantities, discounts and pricing entered on these lines will be removed. Load products from the indents again to start over.',
      confirmText: 'Clear lines',
      cancelText: 'Cancel',
      tone: 'neutral',
      icon: 'bi-eraser',
    });
    if (!proceed) return;
    this.poItems = [];
    this.selectedItems.clear();
    this.clearLineFilters();
    this.updateSummary();
  }

  /** Close the pricing dialog; when it was opened from a grid row, drop the
   *  row's product from the add-line strip so the strip does not linger. */
  closePricingForm(): void {
    this.isPricingFormVisible = false;
    if (this.editingItem) {
      this.editingItem = null;
      this.clearSelectedProduct();
    }
  }

  /** Pricing dialog: apply one of the SKU's known prices to the line being edited. */
  applyKnownPrice(p: any): void {
    if (!this.editingItem || !p) return;
    this.editingItem.costPrice = p.costPrice;
    if (p.mrp != null) this.editingItem.mrp = Number(p.mrp);
    if (p.sellingPrice != null) this.editingItem.sellingPrice = Number(p.sellingPrice);
    this.onQuantityChange(this.editingItem);
    this.updateSummary();
    this.closePricingForm();
  }

  async removeSelectedItems(): Promise<void> {
    const count = this.selectedCount;
    if (!count) return;
    const proceed = await this.confirmDialog.confirm({
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${count} selected item${count > 1 ? 's' : ''}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!proceed) return;
    for (let i = this.poItems.length - 1; i >= 0; i--) {
      if (this.selectedItems.has(this.poItems[i])) {
        this.poItems.splice(i, 1);
      }
    }
    this.selectedItems.clear();
    this.updateSummary();
  }

  onQuantityChange(item: any): void {
    const qty = Number(item.quantity || 0);
    const indentQty = Number(item.indentQuantity || 0);
    // freeQuantity does NOT contribute to the cost, so we don't add it to totalChargedQty
    const freeQty = Number(item.freeQuantity || 0);

    const costPrice = Number(item.costPrice || 0);
    const gstPercent = Number(item.gstPercent || 0);
    const cessRate = Number(item.cessRate || 0);

    // Total quantity that we are paying for
    const totalChargedQty = qty + indentQty;

    // ✅ Robust Sync: Match MRP, Selling Price AND basicCostPrice from item.pricing
    // when the user changes the selected costPrice (dropdown). Each pricing row
    // has its own paired basePrice — we must follow it, not derive.
    if (item.pricing && item.pricing.length > 0) {
      const match = item.pricing.find((p: any) => +p.costPrice === +costPrice);
      if (match) {
        item.mrp = Number(match.mrp || 0);
        item.sellingPrice = Number(match.sellingPrice || 0);
        const matchedBasic = Number(match.basePrice || 0);
        if (matchedBasic > 0) item.actualCostPrice = matchedBasic;
      }
    }

    // Stored basic cost price — never derived from costPrice at runtime.
    const basicCostPrice = this.resolveBasicCostPrice(item);
    item.actualCostPrice = basicCostPrice;

    const breakup = this.computeLineTaxBreakup(
      basicCostPrice,
      costPrice,
      totalChargedQty,
      gstPercent,
      cessRate,
    );
    item.gstAmount = breakup.gstAmount;
    item.cessAmount = breakup.cessAmount;
    item.basicCostTotal = breakup.basicCostTotal;

    // Recompute discount amount from current cost price and discount %
    const discPct = Number(item.discountPercent || 0);
    item.discountAmount = +((costPrice * discPct) / 100).toFixed(2);

    // Total = line gross − line-level discount
    item.total = +(breakup.lineGross - item.discountAmount * totalChargedQty).toFixed(3);

    this.updateSummary();
  }

  /**
   * Single source of truth for PO line tax math (mirrors backend po-tax.util.ts).
   * Both basicCostPrice and costPrice are STORED inputs — neither is derived
   * from the other. The toggle only chooses which one drives the line total
   * to match the vendor's invoice. The per-unit actualCostPrice returned
   * equals the stored basicCostPrice in both modes.
   */
  computeLineTaxBreakup(
    basicCostPrice: number,
    costPrice: number,
    qty: number,
    gstRate: number,
    cessRate: number = 0,
  ): {
    lineGross: number;
    gstAmount: number;
    cessAmount: number;
    basicCostTotal: number;
    actualCostPrice: number;
  } {
    const r2 = (n: number) => +(+n).toFixed(2);
    // r3: price-derived line totals keep 3 decimals so a CP like 85.501
    // survives into the line totals instead of displaying 85.50 while
    // validation sees 85.501. Tax amounts stay at 2 decimals.
    const r3 = (n: number) => +(+n).toFixed(3);
    // r4: per-unit rate retains 4 decimals so derived per-base-unit prices
    // (e.g., per-kg from per-bag) don't lose precision once multiplied by qty.
    const r4 = (n: number) => +(+n).toFixed(4);
    const bp = Number(basicCostPrice) || 0;
    const cp = Number(costPrice) || 0;
    const q = Number(qty) || 0;
    const g = Number(gstRate) || 0;
    const c = Number(cessRate) || 0;
    const totalRate = g + c;
    const inclusive = !!this.poForm?.value?.costPriceIncludesGst;

    if (inclusive) {
      const lineGross = r3(cp * q);
      const gstAmount = r2((lineGross * g) / (100 + totalRate));
      const cessAmount = r2((lineGross * c) / (100 + totalRate));
      const basicCostTotal = r3(lineGross - gstAmount - cessAmount);
      return { lineGross, gstAmount, cessAmount, basicCostTotal, actualCostPrice: r4(bp) };
    }

    const basicCostTotal = r3(bp * q);
    const gstAmount = r2((basicCostTotal * g) / 100);
    const cessAmount = r2((basicCostTotal * c) / 100);
    const lineGross = r3(basicCostTotal + gstAmount + cessAmount);
    return { lineGross, gstAmount, cessAmount, basicCostTotal, actualCostPrice: r4(bp) };
  }

  /**
   * Resolves the per-unit basic cost price (excl GST) for an item.
   * Prefers the stored item.actualCostPrice → variant.pricing[0].basePrice →
   * back-calc from costPrice as last resort (for very old records that
   * predate basePrice being stored).
   */
  private resolveBasicCostPrice(item: any): number {
    const stored = Number(item?.actualCostPrice ?? 0);
    if (stored > 0) return stored;
    const pricing = item?.pricing?.[0] ?? item?.variantData?.pricing?.[0];
    const matched = item?.pricing?.find?.((p: any) => +p.costPrice === +item.costPrice);
    const candidate = Number(matched?.basePrice ?? pricing?.basePrice ?? 0);
    if (candidate > 0) return candidate;
    const cost = Number(item?.costPrice ?? 0);
    const gst = Number(item?.gstPercent ?? 0);
    const cess = Number(item?.cessRate ?? 0);
    return +(cost / (1 + (gst + cess) / 100)).toFixed(4);
  }

  get costPriceIncludesGst(): boolean {
    return !!this.poForm?.value?.costPriceIncludesGst;
  }

  setCostBasis(includesGst: boolean): void {
    if (this.isViewMode || this.costPriceIncludesGst === includesGst) return;
    this.poForm.patchValue({ costPriceIncludesGst: includesGst });
    this.poForm.markAsDirty();
    this.onCostPriceModeChange();
  }

  /** Recompute all line items when the toggle flips. */
  onCostPriceModeChange(): void {
    for (const item of this.poItems) {
      this.onQuantityChange(item);
    }
    this.updateSummary();
  }

  updateSummary(): void {
    let totalItems = this.poItems.length;
    let totalQuantity = 0;
    let totalBasicCost = 0;
    let totalGstAmount = 0;
    let totalPoValue = 0;
    let totalCessAmount = 0;
    let lineItemDiscountAmount = 0;

    this.poItems.forEach((item) => {
      const qty = Number(item.quantity || 0);
      const indentQty = Number(item.indentQuantity || 0);
      const totalChargedQty = qty + indentQty;

      totalQuantity += qty + Number(item.freeQuantity || 0);
      // Use the back-calculated line taxable (CGST Rule 35) — never re-multiply
      // a rounded per-unit base, which would introduce drift.
      totalBasicCost += Number(item.basicCostTotal || 0);
      totalGstAmount += Number(item.gstAmount || 0);
      totalCessAmount += Number(item.cessAmount || 0);
      totalPoValue += Number(item.total || 0);
      lineItemDiscountAmount += Number(item.discountAmount || 0) * totalChargedQty;
    });

    const schemeDiscount = Number(this.poForm.value.schemeDiscountAmount) || 0;
    const fixedDiscount = Number(this.poForm.value.fixedDiscountAmount) || 0;
    const grandTotal = +(totalPoValue - schemeDiscount - fixedDiscount).toFixed(2);

    this.summary = {
      totalItems,
      totalQuantity,
      totalBasicCost: +totalBasicCost.toFixed(2),
      totalGstAmount: +totalGstAmount.toFixed(2),
      totalCessAmount: +totalCessAmount.toFixed(2),
      totalPoValue: +grandTotal.toFixed(2),
      schemeDiscountAmount: schemeDiscount,
      fixedDiscountAmount: fixedDiscount,
      lineItemDiscountAmount: +lineItemDiscountAmount.toFixed(2),
    };
    this.scheduleVendorMappingRefresh();
  }

  getNextPoNumber() {
    this.poService
      .getNextPoNumber(this.selectedWarehouse?.storeId ?? undefined)
      .subscribe((res: any) => {
        this.poForm.patchValue({ poNumber: res?.poNumber });
      });
  }

  onWarehouseChange(warehouse: WarehouseOption | null) {
    this.selectedWarehouse = warehouse;
    this.sourceLocationId = warehouse?.locationId ?? '';
    // The number prefix follows the chosen warehouse's store code.
    if (this.mode === 'create') {
      this.getNextPoNumber();
    }
  }

  loadPreview(poId: string) {
    console.log('loadPreview called for ID:', poId);
    this.showPreview = true;
    this.isLoadingPreview = true;

    this.poService.downloadPurchaseOrder(poId).subscribe({
      next: (response: any) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          console.error('Empty PDF blob received');
          this.toastr.error('Received empty PDF');
          this.isLoadingPreview = false;
          return;
        }
        console.log('PDF Blob received, size:', blob.size);
        const url = URL.createObjectURL(blob);
        this.rawBlobUrl = url;
        // Add PDF parameters for cleaner view
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          url + '#toolbar=0&navpanes=0&view=FitH',
        );
        this.previewUrl = safeUrl;
        this.isLoadingPreview = false;
        console.log('Preview URL set');
      },
      error: (err) => {
        console.error('Download PO Error:', err);
        this.toastr.error('Failed to load PDF preview');
        this.isLoadingPreview = false;
      },
    });
  }

  closePreview() {
    this.showPreview = false;
    // Clean up blob URL
    if (this.rawBlobUrl) {
      URL.revokeObjectURL(this.rawBlobUrl);
      this.rawBlobUrl = null;
    }
    this.afterSave();
  }

  /**
   * Where to go once a save is done (preview closed, or no preview). Buyers
   * raise several POs back to back, so a new PO comes back to a blank create
   * form; an edit returns to the list as before.
   */
  private afterSave(): void {
    if (this.mode === 'create') {
      this.formReload.restart('/po/create-po');
    } else {
      this.router.navigate(['/po']);
    }
  }

  downloadPdf() {
    if (!this.rawBlobUrl) return;
    const link = document.createElement('a');
    link.href = this.rawBlobUrl;
    link.download = `PO-${this.poForm.get('poNumber')?.value || 'document'}.pdf`;
    link.click();
  }

  printPdf() {
    if (!this.rawBlobUrl) return;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = this.rawBlobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Optional: remove iframe after small delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }

  async submitPo(
    status: 'DRAFT' | 'POSTED' = 'POSTED',
    opts: { resubmit?: boolean } = {},
  ) {
    this.poForm.markAllAsTouched();

    if (this.poForm.invalid) return;
    this.isSaved = true;

    // ✅ FINAL VALIDATION: Ensure MRP >= CP, SP >= CP, and SP <= MRP for all items
    // (equality allowed — zero-margin items can have SP = CP = MRP)
    // Compare at 3 decimals: CP is often derived (base × (1+tax)), and float
    // noise like 84.50000001 vs 84.5 must not fail an equal-looking pair.
    for (const item of this.poItems) {
      const mrp = this.round3(Number(item.mrp || 0));
      const cp = this.round3(Number(item.costPrice || 0));
      const sp = this.round3(Number(item.sellingPrice || 0));

      console.log(`Checking Item ${item.sku}: MRP=${mrp}, CP=${cp}, SP=${sp}`);

      if (mrp < cp) {
        this.toastr.error(`Item ${item.sku} has MRP (₹${mrp}) less than Cost Price (₹${cp}). MRP should be greater than or equal to Cost Price.`, 'Pricing Validation Error');
        return;
      }

      if (sp < cp) {
        this.toastr.error(`Item ${item.sku} has Selling Price (₹${sp}) less than Cost Price (₹${cp}). Selling Price should be greater than or equal to Cost Price.`, 'Pricing Validation Error');
        return;
      }

      if (sp > mrp) {
        this.toastr.error(`Item ${item.sku} has Selling Price (₹${sp}) greater than MRP (₹${mrp}).`, 'Pricing Validation Error');
        return;
      }
    }

    // Vendor ↔ SKU mapping: only a POSTED order is checked (a draft is work
    // in progress). May remove lines, so it runs before the payload is built.
    let mappingDecisions: { map: string[]; skip: string[] } | null = null;
    if (status === 'POSTED') {
      mappingDecisions = await this.collectVendorSkuMappingDecisions();
      if (!mappingDecisions) return;
    }
    if (!this.poItems.length) {
      this.toastr.warning('Add at least one line item.', 'Nothing to submit');
      return;
    }

    const items = this.poItems.map((item) => {
      const marginPercent = Number(this.getMarginPercentage(item));
      const totalItemQty =
        Number(item.quantity || 0) + Number(item.indentQuantity || 0);
      const marginAmount = Number(
        (
          (Number(item.mrp) - Number(item.actualCostPrice)) *
          totalItemQty
        ).toFixed(2),
      );

      const mappedItem: any = {
        productVariantId: item.productVariantId,
        quantity: item.quantity,
        costPrice: Number(item.costPrice),
        actualCostPrice: Number(item.actualCostPrice),
        discountAmount: Number(item.discountAmount || 0),
        discountPercent: Number(item.discountPercent || 0),
        gstAmount: Number(item.gstAmount || 0),
        gstPercent: Number(item.gstPercent),
        cessAmount: Number(item.cessAmount || 0),
        cessRate: Number(item.cessRate),
        mrp: Number(item.mrp),
        totalPrice: Number(item.total),
        freeQuantity: item?.freeQuantity || 0,
        indentQuantity: item?.indentQuantity || 0,
        // id: item.id || null, // Don't send null ID if not valid
      };

      const isValid = this.isValidUUID(item.id);

      // This is the only place 'id' is added
      if (item.id && isValid) {
        mappedItem.id = item.id;
      }
      return mappedItem;
    });

    const payload = {
      // The document number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      storeId: this.selectedWarehouse?.storeId ?? undefined,
      vendorId: this.selectedVendor,
      deliveryDate: this.poForm.value.deliveryDate
        ? new Date(this.poForm.value.deliveryDate).toISOString()
        : new Date().toISOString(),
      expiryDate: this.poForm.value.expiryDate
        ? new Date(this.poForm.value.expiryDate).toISOString()
        : new Date().toISOString(),
      indentDate: this.fromDate,
      roundOffAmount: this.roundOffAmount,
      subTotalAmount: this.summary.totalItems ? this.summary.totalBasicCost : 0, // Handle edge case
      totalGSTAmount: Number(this.summary.totalGstAmount),
      totalCessAmount: this.summary.totalCessAmount,
      totalDiscountAmount:
        this.summary.fixedDiscountAmount + this.summary.schemeDiscountAmount,
      totalItems: this.summary.totalItems,
      totalUnits: this.summary.totalQuantity,
      totalQty: this.totalReceivedQuantity,
      indentIds: this.selectedIndents || [],
      packingCharges: Number(this.poForm.value.packingCharges),
      fixedDiscountAmount: Number(this.poForm.value.fixedDiscountAmount),
      schemeDiscountAmount: Number(this.poForm.value.schemeDiscountAmount),
      freightCharges: this.freightCharges,
      totalAmount: this.summary.totalPoValue,
      costPriceIncludesGst: !!this.poForm.value.costPriceIncludesGst,
      items: items, // Add items to payload
      status: status,
      vendorSkuMapping:
        mappingDecisions && (mappingDecisions.map.length || mappingDecisions.skip.length)
          ? mappingDecisions
          : undefined,
    };

    // Final-submit confirmation. Drafts skip this — only ask before the
    // irreversible action. Uses the same ConfirmDialogService instance the
    // unsaved-changes guard uses, so the visual style matches.
    if (status === 'POSTED' && !opts.resubmit) {
      const proceed = await this.confirmDialog.confirm({
        title:
          this.mode === 'create' ? 'Submit Purchase Order?' : 'Update Purchase Order?',
        message:
          this.mode === 'create'
            ? `Once submitted, line items and pricing are locked and the order is sent to <strong>${this.selectedVendorName || 'the vendor'}</strong>. Amendments will need a revision.`
            : `The updated lines and pricing will be posted to <strong>${this.selectedVendorName || 'the vendor'}</strong>.`,
        confirmText: this.mode === 'create' ? 'Submit PO' : 'Post PO',
        cancelText: 'Cancel',
        tone: 'primary',
        icon: 'bi-arrow-up-right',
        summaryLabel: 'Grand total',
        summaryValue: '₹' + Number(this.summary.totalPoValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      });
      if (!proceed) return;
    }

    if (this.mode === 'create') {
      this.poService.createPo(payload).subscribe({
        next: (res: any) => {
          console.log('PO Create Response:', res);
          this.toastr.success('PO Created Successfully');
          this.toastMappingsApplied(res);
          if (res?.poNumber) this.poForm.patchValue({ poNumber: res.poNumber });
          // Load Preview Overlay
          const createdPoId = res?.id || res?.data?.id;

          if (createdPoId) {
            console.log('Triggering preview for PO:', createdPoId);
            this.loadPreview(createdPoId);
          } else {
            console.warn('No PO ID found in response, skipping preview.');
            // Fallback if no ID returned (should not happen normally)
            this.afterSave();
          }
        },
        error: (err) => {
          console.error('PO Create Error:', err);
          if (this.handleUnmappedSkuError(err, status)) return;
          this.toastr.error(
            typeof err?.error?.message === 'string' ? err.error.message : 'Failed to create PO',
          );
        },
      });
    } else {
      this.poService.updatePo(this.poId, payload).subscribe({
        next: (res: any) => {
          this.toastr.success('PO Updated Successfully');
          this.toastMappingsApplied(res);
          this.loadPreview(this.poId);
        },
        error: (err) => {
          console.error(err);
          if (this.handleUnmappedSkuError(err, status)) return;
          this.toastr.error(
            typeof err?.error?.message === 'string' ? err.error.message : 'Failed to update PO',
          );
        },
      });
    }
  }

  // ── Vendor ↔ SKU mapping ───────────────────────────────────────────────

  private scheduleVendorMappingRefresh(): void {
    this.vendorMappingRefresh$.next();
  }

  /** Lines with a usable variant id, de-duplicated, in grid order. */
  private get lineVariantIds(): string[] {
    return [
      ...new Set(
        this.poItems
          .map((i) => i?.productVariantId || i?.variantId)
          .filter((id: any): id is string => !!id),
      ),
    ];
  }

  /**
   * Ask the server which lines the selected vendor supplies and badge the
   * rest. Runs in the open modes only — in STRICT mode the searches already
   * guarantee it — and never blocks data entry: a failed call just leaves the
   * badges off, the server still checks on submit.
   */
  private refreshVendorMappingBadges(): void {
    if (!this.openVendorScope || this.isViewMode || !this.selectedVendor) {
      this.lastMappingSignature = '';
      return;
    }
    const variantIds = this.lineVariantIds;
    const signature = `${this.selectedVendor}|${variantIds.join(',')}`;
    if (!variantIds.length || signature === this.lastMappingSignature) return;
    this.lastMappingSignature = signature;

    this.mappingCheckPending = true;
    this.poService.previewVendorSkuMapping(this.selectedVendor, variantIds).subscribe({
      next: (preview) => {
        this.mappingCheckPending = false;
        this.vendorSkuMappingMode = preview.mode;
        this.applyMappingPreviewToLines(preview);
      },
      error: () => {
        this.mappingCheckPending = false;
        this.lastMappingSignature = '';
      },
    });
  }

  private applyMappingPreviewToLines(preview: VendorSkuMappingPreview): void {
    const byVariant = new Map<string, VendorSkuMappingItem>();
    for (const item of preview.items)
      for (const vid of item.productVariantIds) byVariant.set(vid, item);
    for (const line of this.poItems) {
      const hit = byVariant.get(line?.productVariantId || line?.variantId);
      if (!hit) continue;
      line.vendorMapped = hit.mapped;
      line.productId = line.productId || hit.productId;
      line.vendorNames = hit.vendors.map((v) => v.name);
    }
  }

  /**
   * Submit step. Resolves the per-SKU decisions to send with the PO, or null
   * when the buyer backed out. Lines the buyer chose to drop are removed here.
   */
  private async collectVendorSkuMappingDecisions(): Promise<{
    map: string[];
    skip: string[];
  } | null> {
    // A server rejection already collected the decisions — use them once.
    if (this.pendingMappingDecisions) {
      const pending = this.pendingMappingDecisions;
      this.pendingMappingDecisions = null;
      return pending;
    }

    const none = { map: [], skip: [] };
    const variantIds = this.lineVariantIds;
    if (!this.selectedVendor || !variantIds.length) return none;

    let preview: VendorSkuMappingPreview;
    try {
      preview = await firstValueFrom(
        this.poService.previewVendorSkuMapping(this.selectedVendor, variantIds),
      );
    } catch {
      // Could not check — let the save go through; the server enforces the
      // rule and its PO_UNMAPPED_SKUS answer reopens this dialog.
      return none;
    }
    this.vendorSkuMappingMode = preview.mode;
    this.applyMappingPreviewToLines(preview);

    const unmapped = preview.items.filter((i) => !i.mapped);
    if (!unmapped.length) return none;

    const result = await this.openVendorSkuMappingDialog({
      purpose: 'submit',
      mode: preview.mode,
      vendorName: preview.vendor?.name || this.selectedVendorName,
      items: unmapped,
      total: preview.total,
    });
    if (!result) return null;

    if (result.removeVariantIds.length) this.removeLinesForVariants(result.removeVariantIds);
    return { map: result.map, skip: result.skip };
  }

  private openVendorSkuMappingDialog(args: {
    purpose: 'submit' | 'review';
    mode: VendorSkuMappingMode;
    vendorName: string;
    items: VendorSkuMappingItem[];
    total: number;
  }): Promise<VendorSkuMappingDialogResult | null> {
    return new Promise((resolve) => {
      // STRICT: the mapping is owned by the mapping screen, so the only way
      // forward here is to drop the lines. Otherwise default to mapping —
      // that is what the open modes exist for.
      const defaultDecision: VendorSkuMappingDecision =
        args.mode === 'STRICT' ? 'remove' : 'map';
      this.mappingDialog = {
        purpose: args.purpose,
        mode: args.mode,
        vendorName: args.vendorName,
        rows: args.items.map((i) => ({ ...i, decision: defaultDecision })),
        total: args.total,
        busy: false,
        resolve,
      };
    });
  }

  /** Decisions offered per row, by mode. */
  get mappingDecisionOptions(): VendorSkuMappingDecision[] {
    switch (this.mappingDialog?.mode) {
      case 'PROMPT':
        return ['map', 'skip', 'remove'];
      case 'REQUIRED':
        return ['map', 'remove'];
      default:
        return [];
    }
  }

  get mappingDialogCounts(): { map: number; skip: number; remove: number } {
    const c = { map: 0, skip: 0, remove: 0 };
    for (const r of this.mappingDialog?.rows ?? []) c[r.decision]++;
    return c;
  }

  get mappingDialogConfirmLabel(): string {
    const d = this.mappingDialog;
    if (!d) return '';
    const c = this.mappingDialogCounts;
    const parts: string[] = [];
    if (c.map) parts.push(`Map ${c.map}`);
    if (c.remove) parts.push(`remove ${c.remove}`);
    const action = parts.length ? parts.join(', ') : 'Continue without mapping';
    if (d.purpose === 'review') return parts.length ? action : 'Done';
    const tail = parts.length ? ' & continue' : '';
    return action + tail;
  }

  /** Nothing left on the order once the removals are applied. */
  get mappingDialogRemovesEverything(): boolean {
    const d = this.mappingDialog;
    if (!d) return false;
    const dropping = new Set(
      d.rows.filter((r) => r.decision === 'remove').flatMap((r) => r.productVariantIds),
    );
    return this.poItems.every((i) => dropping.has(i?.productVariantId || i?.variantId));
  }

  setMappingDecision(row: VendorSkuMappingDialogRow, decision: VendorSkuMappingDecision): void {
    if (!this.mappingDialog || this.mappingDialog.busy) return;
    if (decision !== 'remove' && !this.mappingDecisionOptions.includes(decision)) return;
    row.decision = decision;
  }

  setAllMappingDecisions(decision: VendorSkuMappingDecision): void {
    if (!this.mappingDialog || this.mappingDialog.busy) return;
    if (decision !== 'remove' && !this.mappingDecisionOptions.includes(decision)) return;
    for (const r of this.mappingDialog.rows) r.decision = decision;
  }

  cancelMappingDialog(): void {
    if (!this.mappingDialog || this.mappingDialog.busy) return;
    const d = this.mappingDialog;
    this.mappingDialog = null;
    d.resolve(null);
  }

  async confirmMappingDialog(): Promise<void> {
    const d = this.mappingDialog;
    if (!d || d.busy) return;
    const result: VendorSkuMappingDialogResult = {
      map: d.rows.filter((r) => r.decision === 'map').map((r) => r.productId),
      skip: d.rows.filter((r) => r.decision === 'skip').map((r) => r.productId),
      removeVariantIds: d.rows
        .filter((r) => r.decision === 'remove')
        .flatMap((r) => r.productVariantIds),
    };

    if (d.purpose === 'submit') {
      this.mappingDialog = null;
      d.resolve(result);
      return;
    }

    // "Map now": apply the mappings immediately, drop the removed lines.
    d.busy = true;
    try {
      if (result.map.length) {
        const res = await firstValueFrom(
          this.poService.applyVendorSkuMapping(this.selectedVendor, result.map),
        );
        this.toastr.success(
          `${res.mapped.length} SKU(s) mapped to ${res.vendor?.name || d.vendorName}.`,
          'Vendor SKU mapping',
        );
      }
      if (result.removeVariantIds.length) this.removeLinesForVariants(result.removeVariantIds);
      this.mappingDialog = null;
      this.lastMappingSignature = '';
      this.scheduleVendorMappingRefresh();
      d.resolve(result);
    } catch (err: any) {
      d.busy = false;
      this.toastr.error(
        typeof err?.error?.message === 'string' ? err.error.message : 'Could not map the SKUs.',
      );
    }
  }

  /** Banner action: review and fix the unmapped lines before submitting. */
  async reviewVendorSkuMapping(): Promise<void> {
    if (this.isViewMode || !this.selectedVendor) return;
    const variantIds = this.lineVariantIds;
    if (!variantIds.length) return;
    let preview: VendorSkuMappingPreview;
    try {
      preview = await firstValueFrom(
        this.poService.previewVendorSkuMapping(this.selectedVendor, variantIds),
      );
    } catch {
      this.toastr.error('Could not check the vendor mapping right now.');
      return;
    }
    this.vendorSkuMappingMode = preview.mode;
    this.applyMappingPreviewToLines(preview);
    const unmapped = preview.items.filter((i) => !i.mapped);
    if (!unmapped.length) {
      this.toastr.success(`Every line is mapped to ${preview.vendor?.name || this.selectedVendorName}.`);
      return;
    }
    await this.openVendorSkuMappingDialog({
      purpose: 'review',
      mode: preview.mode,
      vendorName: preview.vendor?.name || this.selectedVendorName,
      items: unmapped,
      total: preview.total,
    });
  }

  private removeLinesForVariants(variantIds: string[]): void {
    const drop = new Set(variantIds);
    const removed = this.poItems.filter((i) => drop.has(i?.productVariantId || i?.variantId));
    if (!removed.length) return;
    for (const item of removed) this.selectedItems.delete(item);
    this.poItems = this.poItems.filter((i) => !removed.includes(i));
    this.updateSummary();
    this.toastr.info(`${removed.length} line(s) removed from the order.`);
  }

  /**
   * The server is the real guard: when it answers PO_UNMAPPED_SKUS (the
   * pre-submit check failed, or the mapping changed meanwhile) the same dialog
   * is shown from its payload and the save is retried with the decisions.
   */
  private handleUnmappedSkuError(err: any, status: 'DRAFT' | 'POSTED'): boolean {
    const body = err?.error;
    if (body?.code !== 'PO_UNMAPPED_SKUS' || !Array.isArray(body.unmapped)) return false;
    void this.recoverFromUnmappedSkuError(body, status);
    return true;
  }

  private async recoverFromUnmappedSkuError(body: any, status: 'DRAFT' | 'POSTED'): Promise<void> {
    const mode: VendorSkuMappingMode =
      body.mode === 'PROMPT' || body.mode === 'REQUIRED' ? body.mode : 'STRICT';
    this.vendorSkuMappingMode = mode;
    const items: VendorSkuMappingItem[] = body.unmapped;
    for (const line of this.poItems) {
      const hit = items.find((i) =>
        i.productVariantIds?.includes(line?.productVariantId || line?.variantId),
      );
      if (hit) {
        line.vendorMapped = false;
        line.vendorNames = (hit.vendors || []).map((v) => v.name);
      }
    }
    const result = await this.openVendorSkuMappingDialog({
      purpose: 'submit',
      mode,
      vendorName: body.vendor?.name || this.selectedVendorName,
      items,
      total: this.poItems.length,
    });
    if (!result) return;
    if (result.removeVariantIds.length) this.removeLinesForVariants(result.removeVariantIds);
    if (!this.poItems.length) {
      this.toastr.info('All lines were removed; nothing left to submit.');
      return;
    }
    this.pendingMappingDecisions = { map: result.map, skip: result.skip };
    await this.submitPo(status, { resubmit: true });
  }

  private toastMappingsApplied(res: any): void {
    const n = Number(res?.vendorSkuMappingsApplied || 0);
    if (n > 0)
      this.toastr.success(
        `${n} SKU(s) mapped to ${this.selectedVendorName || 'the vendor'}.`,
        'Vendor SKU mapping',
      );
  }

  // for ROI
  getMarginPercentage(item: any): string {
    if (item && item.marginPercentage !== undefined && item.marginPercentage !== null) {
      console.log(`[Margin Info] Using stored margin for ${item.sku || 'item'}: ${item.marginPercentage}%`);
      return Number(item.marginPercentage).toFixed(2);
    }
    if (!item || item.actualCostPrice <= 0) return '0.00';
    const margin =
      ((item.mrp - item.actualCostPrice) / item.actualCostPrice) * 100;
    console.log(`[Margin Info] Calculated ROI margin for ${item.sku || 'item'}: ${margin.toFixed(2)}%`);
    return margin.toFixed(2); // two decimal places
  }

  customSearchFn(term: string, item: any): boolean {
    term = term.toLowerCase();
    return (
      item?.longDescription?.toLowerCase()?.includes(term) ||
      item?.skuCode?.toLowerCase()?.includes(term)
    );
  }

  onProductSelected(product: any) {
    this.selectedProduct = product;
    this.selectedVariant = product.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.MRP = this.selectedVariant?.pricing?.[0]?.mrp;
    this.warnIfUnpriced(product, this.selectedVariant);
    // A SKU can be supplied by several vendors (Vendor SKU Mapping), and the
    // search above was already scoped to the vendor the user chose. Only fall
    // back to the product's primary vendor when no vendor is selected yet —
    // overwriting the user's choice would silently re-address the PO.
    if (!this.selectedVendor && product.vendorsId) {
      this.selectedVendor = product.vendorsId;
      this.onVendorChange();
    }
    console.log(`[Product Select] Product: ${product.longDescription}, Margin: ${product.marginPercentage}%, GST: ${product.purchaseGstRate?.value}%`);

    this.populatePriceOptions(product);

    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    });
  }

  /** A SKU may be created with only a barcode; say so instead of prefilling ₹0. */
  private warnIfUnpriced(product: any, variant: any) {
    if (!product || hasPurchasePrice(variant)) return;
    this.toastr.warning(
      priceNotSetMessage(product.sku, product.longDescription),
      'Price required',
    );
  }

  onSearchResultSelected(product: any) {
    if (!product) return;

    this.selectedProduct = product;
    this.selectedVariant = product.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.MRP = this.selectedVariant?.pricing?.[0]?.mrp;
    this.warnIfUnpriced(product, this.selectedVariant);
    // A SKU can be supplied by several vendors (Vendor SKU Mapping), and the
    // search above was already scoped to the vendor the user chose. Only fall
    // back to the product's primary vendor when no vendor is selected yet —
    // overwriting the user's choice would silently re-address the PO.
    if (!this.selectedVendor && product.vendorsId) {
      this.selectedVendor = product.vendorsId;
      this.onVendorChange();
    }
    console.log(`[Search Select] Product: ${product.longDescription}, Margin: ${product.marginPercentage}%, GST: ${product.purchaseGstRate?.value}%`);

    this.populatePriceOptions(product);

    // Focus on quantity input
    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    }, 100);

    // Show success message
    this.toastr.success(
      `Product found: ${product.longDescription}`,
      'Product Selected',
    );
  }

  populatePriceOptions(product: any) {
    if (!product) return;
    const prices: number[] = [];
    const mrps: number[] = [];

    const variants = product.variants || [];
    variants.forEach((v: any) => {
      if (v.pricing) {
        v.pricing.forEach((p: any) => {
          if (p.costPrice) prices.push(+p.costPrice);
          if (p.mrp) mrps.push(+p.mrp);
        });
      }
    });

    this.allCostPrices = [...new Set(prices)].sort((a, b) => a - b);
    this.allMRPs = [...new Set(mrps)].sort((a, b) => a - b);
  }

  confirmAddToTable() {
    if (
      !this.selectedProduct ||
      !this.selectedQuantity ||
      !this.enteredCostPrice
    )
      return;

    // One SKU may appear on several lines as long as the cost price differs
    // (the same PO can order old-price and new-price stock). Only an exact
    // SKU + cost price repeat is stopped — warn and jump to that row so the
    // user edits its quantity instead of creating an identical line.
    const dupIndex = this.poItems.findIndex(
      (item) =>
        (item.productVariantId === this.selectedVariant?.id ||
          (!!item.sku && item.sku === (this.selectedVariant?.sku || this.selectedProduct?.sku))) &&
        +item.costPrice === +this.enteredCostPrice,
    );
    if (dupIndex !== -1) {
      this.toastr.warning(
        `${this.selectedVariant?.sku || this.selectedProduct?.sku || 'This item'} is already in the list at this cost price.`,
        'Item already added',
      );
      this.selectedProduct = null;
      this.selectedVariant = null;
      this.selectedQuantity = 1;
      this.enteredCostPrice = 0;
      this.MRP = 0;
      this.allCostPrices = [];
      this.allMRPs = [];
      setTimeout(() => {
        const el = document.getElementById('po-qty-' + dupIndex) as HTMLInputElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
          el.select();
        }
      }, 100);
      return;
    }

    const existing = this.selectedVariant.pricing.find(
      (p: any) => +p.costPrice === +this.enteredCostPrice,
    );

    const proceed = (pricingList: any[]) => {
      const gstRate = parseFloat(
        this.selectedProduct?.purchaseGstRate?.value || '0',
      );
      const costPrice = +this.enteredCostPrice;
      const match = pricingList.find((p: any) => +p.costPrice === +costPrice) || pricingList[0];
      // basicCostPrice is the stored paired value for this costPrice row.
      const matchedBasic = Number(match?.basePrice || 0);
      const basicCostPrice =
        matchedBasic > 0 ? matchedBasic : +(costPrice / (1 + gstRate / 100)).toFixed(4);
      const breakup = this.computeLineTaxBreakup(
        basicCostPrice,
        costPrice,
        this.selectedQuantity,
        gstRate,
        0,
      );
      const actualCost = breakup.actualCostPrice;
      const basicCostTotal = breakup.basicCostTotal;
      const gstAmount = breakup.gstAmount;
      const total = breakup.lineGross;

      this.poItems.push({
        pricing: pricingList,
        productVariantId: this.selectedVariant.id,
        variantName: this.selectedVariant.variantName,
        quantity: this.selectedQuantity,
        costPrice,
        actualCostPrice: actualCost,
        mrp: Number(match?.mrp || 0),
        sellingPrice: Number(match?.sellingPrice || 0),
        gstPercent: gstRate,
        gstAmount,
        total,
        basicCostTotal,
        innerPackaging: this.selectedProduct?.innerPackaging || 1,
        sku: this.selectedVariant.sku || this.selectedProduct.sku || '',

        // ✅ Store metadata for New Pricing modal support from SKU clicks
        productData: this.selectedProduct,
        variantData: this.selectedVariant,
        marginPercentage: this.selectedProduct?.marginPercentage || 0,
      });

      // Reset full selection state — the form has multiple bindings populated
      // by onSearchResultSelected / onProductSelected and they all need to
      // clear for the search row to return to its initial empty state.
      this.selectedProduct = null;
      this.selectedVariant = null;
      this.selectedQuantity = 1;
      this.enteredCostPrice = 0;
      this.MRP = 0;
      this.allCostPrices = [];
      this.allMRPs = [];
      this.updateSummary();
    };

    if (existing) {
      proceed(this.selectedVariant.pricing);
    } else {
      const last = this.selectedVariant.pricing?.[0];
      const body = {
        ...last,
        costPrice: +this.enteredCostPrice,
        basePrice: +this.enteredCostPrice,
      };
      delete body.id;
      delete body.createdAt;
      delete body.updatedAt;
      body.mrp = Number(body.mrp);
      body.sellingPrice = Number(body.sellingPrice);
      this.productService.addVariantPricing(body).subscribe((res: any) => {
        // append new pricing to local array
        this.selectedVariant.pricing.unshift(body);
        proceed(this.selectedVariant.pricing);
      });
    }
  }
  cancel() {
    this.router.navigate(['/po']);
  }

  formatDateToDDMMYYYY(date: Date | null | undefined): string {
    // Handle cases where the input date might be null, undefined, or an invalid Date object
    if (!date || isNaN(date.getTime())) {
      return ''; // Or throw an error, or return a default string like 'N/A'
    }

    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Month is 0-indexed, so add 1
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  toIsoStringFromDDMMYYYY(dateStr: string): string {
    if (!dateStr) return '';

    // Split "dd-MM-yyyy" into parts
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';

    const [dd, mm, yyyy] = parts;

    // Construct ISO date string "yyyy-MM-dd"
    const isoFormatted = `${yyyy}-${mm}-${dd}`;

    const date = new Date(isoFormatted + 'T00:00:00.000Z');
    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  }
  toIsoStringFromYYYYMMDD(dateStr: string): string {
    if (!dateStr) return '';

    // Split "yyyy-MM-dd" into parts
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';

    const [yyyy, mm, dd] = parts;

    // Basic validation for numeric parts and length
    if (
      isNaN(Number(yyyy)) ||
      isNaN(Number(mm)) ||
      isNaN(Number(dd)) ||
      yyyy.length !== 4 ||
      mm.length !== 2 ||
      dd.length !== 2
    ) {
      return '';
    }

    const isoFormatted = `${yyyy}-${mm}-${dd}`;

    const date = new Date(isoFormatted + 'T00:00:00.000Z');

    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  }

  saveAsDraft(): void {
    this.isSaved = true;
    this.submitPo('DRAFT');
  }

  filterExistingItems() {
    // If no items in table, nothing to filter
    if (this.poItems.length === 0) {
      return;
    }

    // Store original count for logging
    const originalCount = this.poItems.length;

    // Filter items based on current filter selections
    this.poItems = this.poItems.filter((item) => {
      let keepItem = true;

      if (this.selectedManufacturers && this.selectedManufacturers.length > 0) {
        let manufacturerMatch = false;

        if (
          item.manufacturerId &&
          this.selectedManufacturers.includes(item.manufacturerId)
        ) {
          manufacturerMatch = true;
        }

        if (
          !manufacturerMatch &&
          item.manufacturerIds &&
          item.manufacturerIds.length > 0
        ) {
          manufacturerMatch = item.manufacturerIds.some((id: string) =>
            this.selectedManufacturers.includes(id),
          );
        }

        if (!manufacturerMatch) {
          keepItem = false;
        }
      }

      // Check brand filter (only if item passed manufacturer check)
      if (keepItem && this.selectedBrands && this.selectedBrands.length > 0) {
        let brandMatch = false;

        // Check single brandId
        if (item.brandId && this.selectedBrands.includes(item.brandId)) {
          brandMatch = true;
        }

        // Check brandIds array (for products with multiple brands)
        if (!brandMatch && item.brandIds && item.brandIds.length > 0) {
          brandMatch = item.brandIds.some((id: string) =>
            this.selectedBrands.includes(id),
          );
        }

        if (!brandMatch) {
          console.log(`Removing item ${item.variantName} - brand mismatch`, {
            itemBrandId: item.brandId,
            itemBrandIds: item.brandIds,
            itemBrandName: item.brandName,
            selectedBrands: this.selectedBrands,
          });
          keepItem = false;
        } else {
          console.log(`Brand match found for ${item.variantName}`, {
            itemBrandId: item.brandId,
            itemBrandIds: item.brandIds,
            selectedBrands: this.selectedBrands,
          });
        }
      }

      // Check department filter (only if item passed previous checks)
      if (
        keepItem &&
        this.selectedDepartment &&
        this.selectedDepartment !== ''
      ) {
        if (
          !item.departmentId ||
          item.departmentId !== this.selectedDepartment
        ) {
          console.log(
            `Removing item ${item.variantName} - department mismatch`,
            {
              itemDepartmentId: item.departmentId,
              selectedDepartment: this.selectedDepartment,
            },
          );
          keepItem = false;
        }
      }

      // Check category filter (only if item passed previous checks)
      if (keepItem && this.selectedCategory && this.selectedCategory !== '') {
        if (!item.categoryId || item.categoryId !== this.selectedCategory) {
          keepItem = false;
        }
      }

      // Check subcategory filter (only if item passed previous checks)
      if (
        keepItem &&
        this.selectedSubCategories &&
        this.selectedSubCategories.length > 0
      ) {
        if (
          !item.subCategoryId ||
          !this.selectedSubCategories.includes(item.subCategoryId)
        ) {
          keepItem = false;
        }
      }

      if (keepItem) {
        console.log(`✅ Keeping item ${item.variantName}`);
      } else {
        console.log(`❌ Removing item ${item.variantName}`);
      }

      return keepItem;
    });

    // Log filtering results
    const filteredCount = this.poItems.length;
    const removedCount = originalCount - filteredCount;

    console.log(
      `Filtering complete: ${removedCount} items removed, ${filteredCount} items remaining`,
    );

    // Update summary after filtering
    this.updateSummary();

    // Show user feedback if items were removed
    if (removedCount > 0) {
      this.toastr.info(
        `${removedCount} item(s) removed due to filter changes`,
        'Items Filtered',
      );
    }
  }

  isValidUUID(uuid: any): boolean {
    // Note the change in the third and fourth groups from [1-5] and [89ab] to [0-9a-f]
    const permissiveUuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return typeof uuid === 'string' && permissiveUuidRegex.test(uuid);
  }


  onSearchTypeChange() {
    this.searchResults = [];
    this.eanSearchResults = [];
    // Clear current selection when search type changes
    if (this.selectedProduct) {
      this.selectedProduct = null;
    }
  }

  get totalReceivedQuantity(): number {
    return (
      this.poItems?.reduce(
        (sum, item) =>
          sum + Number(item.quantity || 0) + Number(item.indentQuantity || 0) + Number(item.freeQuantity || 0),
        0,
      ) || 0
    );
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isSaved) {
      return true;
    }
    if (this.mode === 'edit') {
      return this.confirmDialog.confirm({
        title: 'Leave Edit Page?',
        message:
          'Are you sure you want to leave <strong>Edit Purchase Order</strong> page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
    }

    // check for unsaved form values or items
    if (this.poForm?.dirty || (this.poItems && this.poItems.length > 0)) {
      // using your confirm dialog service (same one from GRN)
      return this.confirmDialog.confirm({
        title: 'Discard changes?',
        message:
          'You have unsaved changes in the  <strong>Purchase Order</strong>. Are you sure you want to leave this page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
    }

    return true;
  }
  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  // ... existing class properties and methods


  selectAllIndents(): void {
    if (this.isViewMode || !this.indentOptions.length) {
      return;
    }

    // Get all 'id's from the indentOptions array
    const allIndentIds = this.indentOptions.map((indent) => indent.id);

    // Assign them to the selectedIndents array
    this.selectedIndents = allIndentIds;

    this.toastr.info(`${allIndentIds.length} Indents selected.`);
  }

  // --- NEW PRICING MODAL METHODS ---

  openNewPricingForm(item?: any): void {
    this.editingItem = item || null; // ✅ Store reference
    if (item) {
      // Case 1: Item clicked from the PO table
      this.selectedProduct = item.productData;
      this.selectedVariant = item.variantData;
    } else if (!this.selectedProduct) {
      // Case 2: Clicked from the manual product add section but no product selected
      this.toastr.warning('Please select a product first.');
      return;
    }

    // Clear previous errors
    this.newPricingValidationError = null;

    // Initialize with zeros
    this.newPricing = {
      basePrice: 0,
      costPrice: 0,
      sellingPrice: 0,
      mrp: 0,
    };

    // Margin-driven CP starts OFF every time; the % is pre-filled from the
    // product master so enabling it needs no lookup.
    this.useMarginForCp = false;
    this.pricingMarginPercent =
      Number(this.selectedProduct?.marginPercentage) || 0;

    this.isPricingFormVisible = true;
  }

  onMarginSettingChange(): void {
    if (this.useMarginForCp && (Number(this.newPricing.mrp) || 0) > 0) {
      this.calculateNewPricing('mrp');
    } else {
      this.validatePricing();
    }
  }

  getCurrentProductGstRate(): number {
    const gstPercent = parseFloat(
      this.selectedProduct?.purchaseGstRate?.value || '0',
    );
    console.log(`[Tax Info] Selected Product GST: ${gstPercent}%`);
    // Return the rate as a factor (e.g., 5% becomes 0.05)
    return gstPercent / 100;
  }

  calculateNewPricing(changedField: 'base' | 'cost' | 'mrp' | 'sellingPrice'): void {
    // Margin-driven CP is opt-in via the popup checkbox; when it is off the
    // margin is treated as 0 so MRP and CP stay independently editable.
    const marginPercent = this.useMarginForCp
      ? Number(this.pricingMarginPercent) || 0
      : 0;
    const gstRateFactor = this.getCurrentProductGstRate();
    const cessRate = parseFloat(this.selectedProduct?.cessRate || '0') / 100;
    const taxFactor = gstRateFactor + cessRate;

    console.log(`[PO Pricing Calc] Trigger: ${changedField}, Margin: ${marginPercent}%, Tax Factor: ${taxFactor.toFixed(4)}`);

    let basePrice = Number(this.newPricing.basePrice) || 0;
    let costPrice = Number(this.newPricing.costPrice) || 0;
    let mrp = Number(this.newPricing.mrp) || 0;

    // MASTER-DRIVER LOGIC: 
    // 1. If MRP is edited, it ALWAYS calculates Cost Price and Base Price.
    // 2. If Margin is 0, we skip the CP/MRP cross-calculation to allow independent entry.
    // 3. If Cost/Base Price is edited, it only updates MRP if MRP is 0 OR if CP > MRP.

    if (changedField === 'mrp') {
      // From MRP: CP = MRP * (1 - Margin%), Base = CP / (1 + Tax).
      // With the margin flag off, MRP entry leaves CP untouched.
      if (marginPercent > 0) {
        costPrice = mrp * (1 - marginPercent / 100);
        basePrice = costPrice / (1 + taxFactor);
      }
    } else if (changedField === 'cost') {
      // From CP: Base = CP / (1 + Tax)
      basePrice = costPrice / (1 + taxFactor);

      // Update MRP only if it's currently empty/zero OR if we just exceeded it
      if (marginPercent > 0 && (mrp === 0 || costPrice > mrp)) {
        mrp = costPrice / (1 - marginPercent / 100);
      }
    } else if (changedField === 'base') {
      // From Base Price: CP = Base * (1 + Tax)
      costPrice = basePrice * (1 + taxFactor);

      // Update MRP only if it's currently empty/zero OR if we just exceeded it
      if (marginPercent > 0 && (mrp === 0 || costPrice > mrp)) {
        mrp = costPrice / (1 - marginPercent / 100);
      }
    }

    this.newPricing.basePrice = parseFloat(basePrice.toFixed(4));
    this.newPricing.costPrice = parseFloat(costPrice.toFixed(4));
    this.newPricing.mrp = parseFloat(mrp.toFixed(4));

    // Do NOT auto-sync Selling Price — user enters it manually

    this.validatePricing();
  }

  validatePricing(): boolean {
    this.newPricingValidationError = this.getPricingValidationError();
    return this.newPricingValidationError === null;
  }

  /**
   * Selling at cost is allowed, but it is nearly always worth a second look,
   * so the popup flags it without blocking the save.
   */
  get isZeroMarginPricing(): boolean {
    const cp = Number(this.newPricing?.costPrice);
    const sp = Number(this.newPricing?.sellingPrice);
    return cp > 0 && sp > 0 && cp === sp;
  }

  /**
   * Margin hints shown under the price fields, each only once both inputs it
   * needs are positive.
   *   Vendor Margin % (under MRP)            = (MRP − CP) / MRP × 100
   *   Customer Profit Margin % (under SP)    = (SP − CP) / SP × 100
   */
  get vendorMarginPercent(): number | null {
    const cp = Number(this.newPricing?.costPrice) || 0;
    const mrp = Number(this.newPricing?.mrp) || 0;
    if (cp <= 0 || mrp <= 0) return null;
    return ((mrp - cp) / mrp) * 100;
  }

  get customerMarginPercent(): number | null {
    const cp = Number(this.newPricing?.costPrice) || 0;
    const sp = Number(this.newPricing?.sellingPrice) || 0;
    if (cp <= 0 || sp <= 0) return null;
    return ((sp - cp) / sp) * 100;
  }

  /** Round to 3 decimals so derived prices (base × (1+tax)) compare cleanly. */
  private round3(v: number): number {
    return Math.round(v * 1000) / 1000;
  }

  getPricingValidationError(): string | null {
    if (
      !this.newPricing.basePrice ||
      !this.newPricing.costPrice ||
      !this.newPricing.sellingPrice ||
      !this.newPricing.mrp ||
      this.newPricing.basePrice <= 0 ||
      this.newPricing.costPrice <= 0 ||
      this.newPricing.sellingPrice <= 0 ||
      this.newPricing.mrp <= 0
    ) {
      return 'All pricing fields must be positive values.';
    }

    const basePrice = this.round3(Number(this.newPricing.basePrice));
    const costPrice = this.round3(Number(this.newPricing.costPrice));
    const sellingPrice = this.round3(Number(this.newPricing.sellingPrice));
    const mrp = this.round3(Number(this.newPricing.mrp));

    if (basePrice > costPrice) {
      return 'Base Price cannot be greater than Cost Price.';
    }

    if (mrp < costPrice) {
      return 'MRP should always be greater than or equal to Cost Price.';
    }

    if (sellingPrice < costPrice) {
      return 'Selling Price should also be greater than or equal to Cost Price.';
    }

    if (sellingPrice > mrp) {
      return 'Selling Price can be same as MRP but not greater.';
    }

    return null;
  }

  saveNewPricing(): void {
    if (!this.selectedVariant || !this.selectedProduct) return;
    if (!this.validatePricing()) return;

    const body = {
      variantId: this.selectedVariant.id,
      mrp: Number(this.newPricing.mrp),
      costPrice: Number(this.newPricing.costPrice),
      basePrice: Number(this.newPricing.basePrice),
      sellingPrice: Number(this.newPricing.sellingPrice),
      validFrom: new Date().toISOString(),
    };

    this.productService.addVariantPricing(body).subscribe({
      next: (res: any) => {
        this.toastr.success('New pricing added successfully!', 'Success');

        if (!this.selectedVariant.pricing) {
          this.selectedVariant.pricing = [];
        }
        this.selectedVariant.pricing.unshift(res);

        this.enteredCostPrice = res.costPrice;
        this.MRP = res.mrp;

        this.populatePriceOptions(this.selectedProduct);

        // 3. Update the specific item in the table if it was passed
        if (this.editingItem) {
          this.editingItem.costPrice = res.costPrice;
          this.editingItem.mrp = res.mrp;
          this.editingItem.sellingPrice = res.sellingPrice;

          this.onQuantityChange(this.editingItem);
        }

        this.isPricingFormVisible = false;
        this.editingItem = null;
      },
      error: (err) => {
        this.toastr.error('Failed to add new pricing.', 'Error');
        console.error('Error adding pricing:', err);
      },
    });
  }

}

